/**
 * This file was auto-generated by Fern from our API Definition.
 */

import * as core from '../../../../core';
import { mergeHeaders, mergeOnlyDefinedHeaders } from '../../../../core/headers';
import * as environments from '../../../../environments';
import * as errors from '../../../../errors/index';
import * as serializers from '../../../../serialization/index';
import * as Truvity from '../../../index';

export declare namespace Desk {
    export interface Options {
        environment?: core.Supplier<environments.TruvityEnvironment | string>;
        /** Specify a custom URL to connect the client to. */
        baseUrl?: core.Supplier<string>;
        apiKey?: core.Supplier<string | undefined>;
        /** Additional headers to include in requests. */
        headers?: Record<string, string | core.Supplier<string | undefined> | undefined>;
        fetcher?: core.FetchFunction;
    }

    export interface RequestOptions {
        /** The maximum time to wait for a response in seconds. */
        timeoutInSeconds?: number;
        /** The number of times to retry the request. Defaults to 2. */
        maxRetries?: number;
        /** A hook to abort the request. */
        abortSignal?: AbortSignal;
        /** Additional query string parameters to include in the request. */
        queryParams?: Record<string, unknown>;
        /** Additional headers to include in the request. */
        headers?: Record<string, string | core.Supplier<string | undefined> | undefined>;
    }
}

/**
 * The API to manage DID Documents and to receive DIDComm messages.
 */
export class Desk {
    protected readonly _options: Desk.Options;

    constructor(_options: Desk.Options = {}) {
        this._options = _options;
    }

    /**
     * Get the published JSON Schema for a given slug and version. The slug is the unique identifier for the schema, and the version is the specific version of the schema.
     *
     * @param {string} tenantId - The ID of the tenant.
     * @param {string} slug - Slug of a CredentialSchema.
     * @param {number} version - The version of the resource.
     * @param {Truvity.PublishedJsonSchemaFetchRequest} request
     * @param {Desk.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.desk.publishedJsonSchemaFetch("tenant_id", "slug", 1000000)
     */
    public publishedJsonSchemaFetch(
        tenantId: string,
        slug: string,
        version: number,
        request: Truvity.PublishedJsonSchemaFetchRequest = {},
        requestOptions?: Desk.RequestOptions,
    ): core.HttpResponsePromise<Record<string, unknown>> {
        return core.HttpResponsePromise.fromPromise(
            this.__publishedJsonSchemaFetch(tenantId, slug, version, request, requestOptions),
        );
    }

    private async __publishedJsonSchemaFetch(
        tenantId: string,
        slug: string,
        version: number,
        request: Truvity.PublishedJsonSchemaFetchRequest = {},
        requestOptions?: Desk.RequestOptions,
    ): Promise<core.WithRawResponse<Record<string, unknown>>> {
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `tenants/${encodeURIComponent(tenantId)}/json-schema/${encodeURIComponent(slug)}/v${encodeURIComponent(version)}`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.desk.publishedJsonSchemaFetch.Response.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling GET /tenants/{tenant_id}/json-schema/{slug}/v/{version}.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Get the published Meta Schema for a given slug and version. The slug is the unique identifier for the schema.
     *
     * @param {string} tenantId - The ID of the tenant.
     * @param {string} slug - Slug of a CredentialSchema.
     * @param {number} version - The version of the resource.
     * @param {Truvity.PublishedMetaSchemaFetchRequest} request
     * @param {Desk.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.desk.publishedMetaSchemaFetch("tenant_id", "slug", 1000000)
     */
    public publishedMetaSchemaFetch(
        tenantId: string,
        slug: string,
        version: number,
        request: Truvity.PublishedMetaSchemaFetchRequest = {},
        requestOptions?: Desk.RequestOptions,
    ): core.HttpResponsePromise<Truvity.PublishedCredentialSchema> {
        return core.HttpResponsePromise.fromPromise(
            this.__publishedMetaSchemaFetch(tenantId, slug, version, request, requestOptions),
        );
    }

    private async __publishedMetaSchemaFetch(
        tenantId: string,
        slug: string,
        version: number,
        request: Truvity.PublishedMetaSchemaFetchRequest = {},
        requestOptions?: Desk.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.PublishedCredentialSchema>> {
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `tenants/${encodeURIComponent(tenantId)}/meta-schema/${encodeURIComponent(slug)}/v${encodeURIComponent(version)}`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.PublishedCredentialSchema.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling GET /tenants/{tenant_id}/meta-schema/{slug}/v/{version}.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Get the published VCDM JSON Schema for a given slug and version. The slug is the unique identifier for the schema, and the version is the specific version of the schema.
     *
     * @param {string} tenantId - The ID of the tenant.
     * @param {string} slug - Slug of a CredentialSchema.
     * @param {number} version - The version of the resource.
     * @param {Truvity.PublishedVcdmJsonSchemaFetchRequest} request
     * @param {Desk.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.desk.publishedVcdmJsonSchemaFetch("tenant_id", "slug", 1000000)
     */
    public publishedVcdmJsonSchemaFetch(
        tenantId: string,
        slug: string,
        version: number,
        request: Truvity.PublishedVcdmJsonSchemaFetchRequest = {},
        requestOptions?: Desk.RequestOptions,
    ): core.HttpResponsePromise<Record<string, unknown>> {
        return core.HttpResponsePromise.fromPromise(
            this.__publishedVcdmJsonSchemaFetch(tenantId, slug, version, request, requestOptions),
        );
    }

    private async __publishedVcdmJsonSchemaFetch(
        tenantId: string,
        slug: string,
        version: number,
        request: Truvity.PublishedVcdmJsonSchemaFetchRequest = {},
        requestOptions?: Desk.RequestOptions,
    ): Promise<core.WithRawResponse<Record<string, unknown>>> {
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `tenants/${encodeURIComponent(tenantId)}/vc-json-schema/${encodeURIComponent(slug)}/v${encodeURIComponent(version)}`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.desk.publishedVcdmJsonSchemaFetch.Response.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling GET /tenants/{tenant_id}/vc-json-schema/{slug}/v/{version}.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Get the published JSON-LD Vocab for a given slug and version. The slug is the unique identifier for the schema, and the version is the specific version of the schema.
     *
     * @param {string} tenantId - The ID of the tenant.
     * @param {string} slug - Slug of a CredentialSchema.
     * @param {number} version - The version of the resource.
     * @param {Truvity.PublishedJsonLdVocabFetchRequest} request
     * @param {Desk.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.desk.publishedJsonLdVocabFetch("tenant_id", "slug", 1000000)
     */
    public publishedJsonLdVocabFetch(
        tenantId: string,
        slug: string,
        version: number,
        request: Truvity.PublishedJsonLdVocabFetchRequest = {},
        requestOptions?: Desk.RequestOptions,
    ): core.HttpResponsePromise<Record<string, unknown>> {
        return core.HttpResponsePromise.fromPromise(
            this.__publishedJsonLdVocabFetch(tenantId, slug, version, request, requestOptions),
        );
    }

    private async __publishedJsonLdVocabFetch(
        tenantId: string,
        slug: string,
        version: number,
        request: Truvity.PublishedJsonLdVocabFetchRequest = {},
        requestOptions?: Desk.RequestOptions,
    ): Promise<core.WithRawResponse<Record<string, unknown>>> {
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `tenants/${encodeURIComponent(tenantId)}/vocab/${encodeURIComponent(slug)}/v${encodeURIComponent(version)}`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.desk.publishedJsonLdVocabFetch.Response.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling GET /tenants/{tenant_id}/vocab/{slug}/v/{version}.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Get DID-Document for [did:web Method Specification](https://w3c-ccg.github.io/did-method-web/).
     *
     * @param {string} tenantId - The ID of the tenant.
     * @param {Truvity.DidDocumentGetRequest} request
     * @param {Desk.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.desk.didDocumentGet("tenant_id")
     */
    public didDocumentGet(
        tenantId: string,
        request: Truvity.DidDocumentGetRequest = {},
        requestOptions?: Desk.RequestOptions,
    ): core.HttpResponsePromise<Truvity.DidDocument> {
        return core.HttpResponsePromise.fromPromise(this.__didDocumentGet(tenantId, request, requestOptions));
    }

    private async __didDocumentGet(
        tenantId: string,
        request: Truvity.DidDocumentGetRequest = {},
        requestOptions?: Desk.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.DidDocument>> {
        const { ifNoneMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-None-Match': ifNoneMatch != null ? ifNoneMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `${encodeURIComponent(tenantId)}/did.json`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.DidDocument.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling GET /{tenant_id}/did.json.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Receive DIDComm Message.
     *
     * @param {string} tenantId - The ID of the tenant.
     * @param {Truvity.DidCommMessageRecvRequest} request
     * @param {Desk.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.desk.didCommMessageRecv("tenant_id", {
     *         body: "string"
     *     })
     */
    public didCommMessageRecv(
        tenantId: string,
        request: Truvity.DidCommMessageRecvRequest,
        requestOptions?: Desk.RequestOptions,
    ): core.HttpResponsePromise<void> {
        return core.HttpResponsePromise.fromPromise(this.__didCommMessageRecv(tenantId, request, requestOptions));
    }

    private async __didCommMessageRecv(
        tenantId: string,
        request: Truvity.DidCommMessageRecvRequest,
        requestOptions?: Desk.RequestOptions,
    ): Promise<core.WithRawResponse<void>> {
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `${encodeURIComponent(tenantId)}/didcomm-messages/recv`,
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/didcomm-signed+json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.desk.didCommMessageRecv.Request.jsonOrThrow(request, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return { data: undefined, rawResponse: _response.rawResponse };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling POST /{tenant_id}/didcomm-messages/recv.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    protected async _getCustomAuthorizationHeaders() {
        const apiKeyValue = await core.Supplier.get(this._options.apiKey);
        return { 'X-API-KEY': apiKeyValue };
    }
}
